import { reservedKeys } from '../core/checks'
import type { PluginContext } from '../core/types'
import type { WebAnalyticsOptions } from './index'

const defaultExtensions =
  'pdf xlsx docx txt rtf csv exe key pps ppt pptx 7z pkg rar gz zip avi mov mp4 mpeg wmv midi mp3 wav wma dmg'.split(
    ' ',
  )

const withoutWww = (host: string) =>
  host.startsWith('www.') ? host.slice(4) : host

export function trackClick(
  event: MouseEvent,
  ctx: PluginContext,
  options: WebAnalyticsOptions,
  pageId: string | undefined,
): void {
  if (event.type === 'auxclick' && event.button !== 1) {
    return
  }
  const start = event.composedPath()[0]
  if (!(start instanceof Element)) {
    return
  }
  if (options.dataAttributes) {
    const tagged = start.closest('[data-loomduct-event]')
    if (tagged) {
      const name = tagged.getAttribute('data-loomduct-event') ?? ''
      if (!name.trim() || name.startsWith('$') || name.length > 200) {
        try {
          if (process.env.NODE_ENV !== 'production') {
            console.warn(`[loomduct] data-loomduct-event not allowed: ${name}`)
          }
        } catch {}
        return
      }
      const properties: Record<string, string> = {}
      let count = 0
      for (const attribute of tagged.attributes) {
        const key = attribute.name.startsWith('data-loomduct-')
          ? attribute.name.slice(14)
          : ''
        if (
          key &&
          key !== 'event' &&
          key !== 'not-found' &&
          key.length <= 128 &&
          !reservedKeys.has(key) &&
          count < 30
        ) {
          properties[key] = attribute.value.slice(0, 2000)
          count += 1
        }
      }
      ctx.send({ name, properties, pageId }, { beacon: true })
      return
    }
  }
  const link = start.closest('a')
  const href = link?.getAttribute('href')
  if (!link || !href) {
    return
  }
  let url: URL
  try {
    url = new URL(href, document.baseURI)
  } catch {
    return
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return
  }
  const extensions = Array.isArray(options.fileDownloads)
    ? options.fileDownloads
    : defaultExtensions
  const path = url.pathname.toLowerCase()
  if (
    options.fileDownloads &&
    (link.hasAttribute('download') ||
      extensions.some((extension) =>
        path.endsWith(`.${extension.toLowerCase()}`),
      ))
  ) {
    ctx.send(
      { name: '$file_download', target: url.href, pageId },
      { beacon: true },
    )
    return
  }
  if (
    options.outboundLinks &&
    withoutWww(url.hostname) !== withoutWww(location.hostname)
  ) {
    ctx.send(
      { name: '$outbound_click', target: url.href, pageId },
      { beacon: true },
    )
  }
}
